import { ReferenceLocation } from '@/types';
import { POST_RESOLVE_LOCATIONS_API_ENDPOINT } from '@/constants';
import { withServerTiming } from '@/lib/serverTiming';
import { logSsrFetch } from '@/lib/ssrLog';

type LocationInput = Pick<ReferenceLocation, 'type' | 'code' | 'name'> & { postcode?: string };

/**
 * Server-side version of the /reference/resolve call.
 * Returns the enriched locations, or the original stubs on any failure so the
 * page can still render (names are available from the URL).
 * postcode is included only for PLACE locations (backend contract, PR #294).
 */
export const fetchResolveLocationsSsr = async (
  locations: ReferenceLocation[],
): Promise<ReferenceLocation[]> => {
  if (!locations.length) return locations;

  const input: LocationInput[] = locations.map(({ type, code, name, postcode }) => ({
    type,
    code,
    name,
    ...(type === 'PLACE' && postcode ? { postcode } : {}),
  }));

  return withServerTiming('fetchResolveLocationsSsr', async () => {
    try {
      const url = `${process.env.NEXT_PUBLIC_API_URL}${POST_RESOLVE_LOCATIONS_API_ENDPOINT}`;
      const start = performance.now();
      const res = await fetch(url, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      logSsrFetch({ url, timing: performance.now() - start, status: res.status });

      if (!res.ok) return locations;

      return (await res.json()) as ReferenceLocation[];
    } catch {
      return locations;
    }
  });
};
